import ArrowIcon from '@mui/icons-material/ArrowForwardRounded';
import { Button, Typography } from '@mui/material';

import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/currency';

/**
 * What the director owes, and the one button that clears it.
 *
 * The page opens on the answer to the only question a director arrives with —
 * *how much, and how many* — and then offers to walk them through all of it.
 * The old screen opened on a search field, a type filter and a date range,
 * which asked the director to narrow a list before it would tell them anything.
 *
 * The amount is set large and tight because it is the fact the whole screen is
 * about; everything below is quieter by comparison. That contrast is the
 * design's one deliberate piece of loudness, and nothing else competes with it.
 */

interface OutstandingHeroProps {
  amountDue: number;
  /** Actionable items only — things waiting on co-directors are not work. */
  taskCount: number;
  /** Items on screen that are not this director's to act on, i.e. waiting on a
   *  co-director. Only read when `taskCount` is 0, to tell "nothing needs you,
   *  but work is in flight" apart from "nothing here at all". */
  waitingCount: number;
  onStart: () => void;
}

const waitingSentence = (count: number): string =>
  count === 1
    ? '1 item is waiting on someone else'
    : `${String(count)} items are waiting on someone else`;

export const OutstandingHero = ({
  amountDue,
  taskCount,
  waitingCount,
  onStart,
}: OutstandingHeroProps) => {
  // Money can be outstanding on an item this director cannot act on yet, so the
  // amount alone does not mean there is work — `taskCount` is what decides
  // whether the hero offers the flow. The FIGURE is not conditional on it: a
  // director whose items are all with co-directors still owes what they owe,
  // and telling them "nothing" over a list of priced rows contradicts itself.
  const hasWork = taskCount > 0;
  const owesMoney = amountDue > 0;
  const tasks = taskCount === 1 ? '1 thing needs you' : `${String(taskCount)} things need you`;

  // The headline is the amount when money is owed, the task count when there is
  // work but nothing to pay, and the settled state otherwise.
  let headline = 'Nothing needs your attention';
  if (owesMoney) headline = formatCurrency(amountDue);
  else if (hasWork) headline = tasks;

  // Names the landmark with the headline, so the panel is reachable as a region
  // rather than being an anonymous <section> a screen reader skips over. The
  // headline is the one line always present — the "Total due" eyebrow above it
  // is not — so it is what the region is named by.
  const labelId = 'outstanding-hero-label';

  return (
    <section
      aria-labelledby={labelId}
      className={cn(
        'relative overflow-hidden rounded-3xl px-5 py-5',
        'flex flex-col gap-4',
        // Inverted, and the only dark surface on the page. The hero is the
        // screen's subject rather than one card among several — everything
        // below is a breakdown of this figure — so it is separated from the
        // working by weight.
        //
        // A wide diagonal band of light crosses the middle with both corners
        // falling away dark, so the panel reads as LIT rather than filled. The
        // stops run long — the light climbs across most of the panel instead
        // of spiking at the centre line — because a narrow band reads as a
        // seam, and a broad one reads as a light source.
        'bg-linear-160 from-hero-from from-5% via-hero-via via-52% to-hero-to to-95% text-white',
        'shadow-raised',
      )}
    >
      {/* The specular streak riding the crest of the band, a little past the
          centre so the brightest line falls BELOW the headline rather than
          through it — white ink needs the dark end of the ramp to stay
          readable. Purely visual, so it is hidden from the a11y tree. */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0',
          'bg-[linear-gradient(160deg,transparent_20%,rgba(255,255,255,0.22)_56%,transparent_88%)]',
        )}
      />

      {/* The copy sits over the bright half of the band, where white ink alone
          would fall under AA. A scrim anchored to the text corner buys the
          contrast back without flattening the gradient — darkening the whole
          panel to make the text safe would undo the light it was built for. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_100%_at_0%_0%,rgba(46,46,46,0.82)_0%,rgba(46,46,46,0.40)_45%,transparent_75%)]"
      />

      <div className="relative flex flex-col gap-1">
        {/* Only when money is owed. "Nothing needs your attention" is already a
            complete sentence, and an "Outstanding" label above it labelled the
            absence of anything outstanding. */}
        {owesMoney && (
          <Typography
            variant="caption"
            component="p"
            className="font-semibold tracking-[0.08em] text-white/70 uppercase"
          >
            Total due
          </Typography>
        )}

        <Typography
          id={labelId}
          component="p"
          className={cn(
            'leading-none font-bold tracking-[-0.03em]',
            // A currency figure is short and benefits from the full display
            // size. The task sentence in the same slot is several times longer,
            // so it is set smaller — and smaller again on a phone, where
            // `2.5rem` wrapped it across three lines.
            owesMoney ? 'text-[2.5rem] tabular-nums' : 'text-[1.5rem] sm:text-[1.875rem]',
          )}
        >
          {headline}
        </Typography>

        {/* The second line says what IS happening, so a director who can see
            items on the page below is not told "nothing" and left wondering
            whether the list is wrong. */}
        {(owesMoney || !hasWork) && (
          <Typography variant="body2" className="text-white/70">
            {hasWork ? tasks : waitingSentence(waitingCount)}
          </Typography>
        )}
      </div>

      {/* No button when there is nothing to do: "Get it done" would open a flow
          with no steps. The panel still carries the status, which is the point
          — a director whose work is all with co-directors gets an answer here
          rather than being left to infer it from the list. */}
      {hasWork && (
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowIcon />}
          onClick={onStart}
          // Inverted with the panel. Everywhere else the action is the darkest
          // object on a pale screen; on the one dark surface that same rule
          // makes it the palest, because a dark fill here would sink into the
          // gradient behind it. The weight GAP is what carries the meaning,
          // not the colour, so it survives the inversion.
          className="relative w-full rounded-full bg-white text-brand-900 hover:bg-brand-100 sm:w-auto sm:self-start"
        >
          Get it done
        </Button>
      )}
    </section>
  );
};
